import React, { useMemo } from 'react';
import { Box, IconButton, Typography, Divider, Table, TableBody, TableCell, TableRow, Tooltip, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { FlyToInterpolator, type PickingInfo } from '@deck.gl/core';
import type { GeoJsonFeature } from '../../utils/ogc';
import MiniDepthChart from '../charts/MiniDepthChart';
import BrushedMiniDepthChart from '../charts/BrushedMiniDepthChartNew';
import { useEventWithDepthData } from '../../layers/event_with_depth_data';
import { usePastFloodEvents } from '../../layers/past_flood_events';
// import CalendarHeatmap from './CalendarHeatmap';
import CalendarHeatmap from '../calendar/VisxCalendarHeatmap';
import LocationPinIcon from '@mui/icons-material/LocationPin';
import EventIcon from '@mui/icons-material/Event';
import { format, formatRelative } from 'date-fns';
import { depthToColor } from '../../utils/layer_style';
import StreetView from '../maps/StreetView';
import MetadataList from './MetadataList';


export default function SensorDetailContent({ 
    info, timeRange, currentTime, 
    onClose, 
    onSelectRange,
    setInitialViewState,
  }: { 
    info: PickingInfo<GeoJsonFeature>; timeRange: [Date, Date, boolean]; currentTime?: Date; onClose: () => void; onSelectRange?: (timeRange: [Date, Date]) => void; setInitialViewState: (viewState: { latitude: number; longitude: number; zoom: number; bearing: number; pitch: number; }) => void;   }) {
  const f = info?.object as GeoJsonFeature;
  const p = info?.object?.properties as GeoJsonFeature['properties'];
  const seriesFeatures = React.useMemo(() => info.object ? [info.object] : [], [info.object]);
  // const now = currentTime ?? new Date();
  // const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
  // const endOfToday = new Date();
  // endOfToday.setHours(23, 59, 59, 999);
  const endOfToday = useMemo(() => {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d;
  }, []);
  // const endOfTomorrow = useMemo(() => {
  //   const d = new Date();
  //   d.setDate(d.getDate() + 1);
  //   d.setHours(23, 59, 59, 999);
  //   return d;
  // }, []);
  const fromTime = info.object?.properties.date_deployed //React.useMemo(() => { const d = new Date(now); d.setDate(d.getDate() - 365); return d; }, [now]);
  const toTime = info.object?.properties.date_down || endOfToday;
  const pastEvents = info.object ? info.object.properties.past_events || [] : [];
  const pastEventsForHeatmap = useMemo(() => {
    const pastEvents = info.object ? info.object.properties.past_events || [] : [];
    return pastEvents.map((e) => ({ date: e.properties.start_time, endDate: e.properties.end_time, value: Number(e.properties.max_depth_inches), label: e.properties.name }));
  }, [info.object]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
      <Box sx={{ display: 'flex', alignItems: 'top', justifyContent: 'space-between', px: 2, py: 1.5 }}>
        <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, pr: 2 }} noWrap>
          {p?.name || p?.deployment_id || 'Sensor'}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
          {p.borough && <Typography variant="body2" component="span">
            <LocationPinIcon sx={{ fontSize: 13 }} /> {p.neighborhood} {p.borough}
          </Typography>} <Typography variant="caption" component="span" sx={{ color: 'text.secondary' }}>({f?.geometry?.coordinates?.slice().reverse().map((c: number) => c.toFixed(4)).join(', ')})</Typography>
        </Box>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          Last Measurement: <b>{p.last_measurement_time ? formatRelative(new Date(p.last_measurement_time), new Date()) : 'N/A'}</b> {p.last_depth_inches != null && `- ${p.last_depth_inches.toFixed(1)} in`}
        </Typography>
        <IconButton onClick={() => {
          setInitialViewState?.(v => ({
            latitude: f.geometry.coordinates[1],
            longitude: f.geometry.coordinates[0],
            zoom: 17,
            // bearing: 0,
            pitch: 60 - (Math.random() ** 2 + 0.1) as number,
            transitionInterpolator: new FlyToInterpolator({ speed: 2 }),
            transitionDuration: 'auto'
          }));
        }} size="small" aria-label="Center map on sensor" sx={{ mt: 0.5 }}>
          <Tooltip title="Zoom to sensor" arrow>
            <LocationPinIcon fontSize="small" />
          </Tooltip>
        </IconButton>
        {/* {p.start_time && <div><EventIcon sx={{ fontSize: 13 }} /> Deployed On: <b>{format(new Date(p.start_time), 'MMMM d, yyyy')}</b></div>} */}
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Close details" sx={{
            position: 'absolute',
            top: 8,
            right: 8,
        }}><CloseIcon /></IconButton>
      </Box>
      <Divider />
      <Box sx={{ px: 2, py: 1.5, overflowY: 'auto' }}>
        {/* Time range indicators */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            From: <b>{format(timeRange[0], 'PP p')}</b>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            To: <b>{format(timeRange[1], 'PP p')}</b>
          </Typography>
        </Box>
        {seriesFeatures && seriesFeatures.length > 0 && (
          // <MiniDepthChart
          //   timeRange={timeRange}
          //   currentTime={currentTime}
          //   features={seriesFeatures}
          //   highlightedKey={p?.deployment_id || p?.id}
          //   height={120}
          // />
          <BrushedMiniDepthChart
            timeRange={timeRange}
            height={200}
            currentTime={currentTime}
            features={seriesFeatures}
            // highlightedKey={p?.deployment_id || p?.id}
            // expanded={expanded}
            // setExpanded={setExpanded}
          />
        )}

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Past Flood Events
              <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>({pastEvents.length} events)</Typography>
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Deployed: <Tooltip title="Date when the sensor was deployed" arrow><b>{format(new Date(p.date_deployed), 'MMMM d, yyyy')}</b></Tooltip> to {p.date_down ? <Tooltip title="Date when the sensor was taken down" arrow><b>{format(new Date(p.date_down), 'MMMM d, yyyy')}</b></Tooltip> : <Tooltip title="Sensor is still up" arrow><b>now</b></Tooltip>}
            </Typography>
            {/* {pastEvents && <CalendarHeatmap
            //   events={pastEvents.filter((e: any) => {
            //     if (p?.deployment_id && e?.properties?.deployment_id) return e.properties.deployment_id === p.deployment_id;
            //     const ec = e?.geometry?.coordinates || [];
            //     const sc = (info.object as any)?.geometry?.coordinates || [];
            //     if (!ec.length || !sc.length) return false;
            //     return Math.abs(ec[0] - sc[0]) < 1e-4 && Math.abs(ec[1] - sc[1]) < 1e-4;
            //   }).map((e: any) => ({ date: new Date(e.properties.start_time), weight: Math.max(1, Number(e.properties.max_depth_inches) || 1) }))}
            events={pastEventsForHeatmap}
                fromTime={fromTime}
                toTime={toTime}
                unit="in"
                decimals={1}
                onSelectRange={onSelectRange}
                selectionFrom={timeRange[0]}
                selectionTo={timeRange[1]}
                units='"'
            />} */}
        {/* {pastEvents && pastEvents.length > 0 ? (

          ) : (
            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>No flood events recorded for this sensor.</Typography>
          )} */}
          </Box>

          {pastEvents && pastEvents.length > 0 && (
            <PastEventList pastEvents={pastEvents} onSelectRange={onSelectRange} />
          )}

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Metadata</Typography>
          {/* {info.object && <MetadataList1 feature={info.object} />} */}
          {info.object && <MetadataList feature={info.object} />}
          <StreetView feature={info.object as GeoJsonFeature} />
        </Box>
      </Box>
    </Box>
  );
}


const PastEventList = React.memo(function PastEventList({ pastEvents, onSelectRange }: { pastEvents: GeoJsonFeature[], onSelectRange?: (timeRange: [Date, Date]) => void }) {
  return (
    <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Deepest Floods</Typography>
              <Table size="small" sx={{ '& td, & th': { py: 0.5, fontSize: 13 } }}>
                <TableBody>
                  {pastEvents
                    .slice()
                    .sort((a: any, b: any) => (b.properties.max_depth_inches - a.properties.max_depth_inches))
                    .slice(0, 10)
                    .map((e: any, idx: number) => {
                      const depth = Number(e.properties.max_depth_inches) || 0;
                      const [r, g, b] = depthToColor(depth);
                      return (
                        <TableRow
                          key={e.id || idx}
                          hover
                          sx={{ cursor: 'pointer' }}
                          // onClick={() => {
                          //   const start: Date = new Date(e.properties.start_time);
                          //   const end: Date = new Date(e.properties.end_time || e.properties.start_time);
                          //   onSelectRange?.(start, end);
                          // }}
                        >
                          <TableCell width={44} align="center">
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 28,
                                height: 28,
                                borderRadius: '50%',
                                background: `rgba(${r},${g},${b},0.9)`,
                                color: '#fff',
                                fontWeight: 800,
                                fontSize: 12,
                                lineHeight: 1,
                              }}
                            >
                              {depth >= 10 ? depth.toFixed(0) : depth.toFixed(1)}
                            </span>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{format(new Date(e.properties.start_time), 'Pp')}</Typography>
                            <Typography variant="caption" color="text.secondary">to {format(new Date(e.properties.end_time), 'Pp')}</Typography>
                          </TableCell>
                          {/* <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 700 }}>{depth.toFixed(1)} in</TableCell> */}
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>
                            <Tooltip title="View the map at this point in time" arrow>
                              <Button variant="text" size="small" onClick={(ev) => {
                                const start: Date = new Date(e.properties.start_time.getTime() - 3 * 60 * 60 * 1000);
                                const end: Date = new Date((e.properties.end_time || (
                                  e.properties.start_time.getTime() < Date.now() - 24 * 60 * 60 * 1000 ? 
                                    new Date(e.properties.start_time.getTime() + 8 * 60 * 60 * 1000) :
                                    new Date()
                                )).getTime() + 3 * 60 * 60 * 1000);
                                onSelectRange?.([start, end]);
                              }}>
                                Set Map
                              </Button>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </Box>
  );
});


// export const MetadataList1 = React.memo(function MetadataList1({ feature }: { feature: GeoJsonFeature }) {
//   const p = feature?.properties || {};  
//   return (
//     <Table size="small" sx={{ '& td': { py: 0.5 } }}>
//             <TableBody>
//               {p?.deployment_id && (
//                 <TableRow>
//                   <TableCell component="th" scope="row">Deployment ID</TableCell>
//                   <TableCell>{p.deployment_id}</TableCell>
//                 </TableRow>
//               )}
//               {p?.name && (
//                 <TableRow>
//                   <TableCell component="th" scope="row">Name</TableCell>
//                   <TableCell>{p.name}</TableCell>
//                 </TableRow>
//               )}
//               {p?.neighborhood && (
//                 <TableRow>
//                   <TableCell component="th" scope="row">Neighborhood</TableCell>
//                   <TableCell>{p.neighborhood}</TableCell>
//                 </TableRow>
//               )}
//               {p?.borough && (
//                 <TableRow>
//                   <TableCell component="th" scope="row">Borough</TableCell>
//                   <TableCell>{p.borough}</TableCell>
//                 </TableRow>
//               )}
//               {p?.date_deployed && (
//                 <TableRow>
//                   <TableCell component="th" scope="row">Date Deployed</TableCell>
//                   <TableCell>{new Date(p.date_deployed).toLocaleString()}</TableCell>
//                 </TableRow>
//               )}
//               {p?.date_down && (
//                 <TableRow>
//                   <TableCell component="th" scope="row">Date Down</TableCell>
//                   <TableCell>{new Date(p.date_down).toLocaleString()}</TableCell>
//                 </TableRow>
//               )}
//               {p?.sensor_mount && (
//                 <TableRow>
//                   <TableCell component="th" scope="row">Sensor Mount</TableCell>
//                   <TableCell>{p.sensor_mount}</TableCell>
//                 </TableRow>
//               )}
//               {p?.mounted_over && (
//                 <TableRow>
//                   <TableCell component="th" scope="row">Mounted Over</TableCell>
//                   <TableCell>{p.mounted_over}</TableCell>
//                 </TableRow>
//               )}
//               {p?.flood_event_count != null && (
//                 <TableRow>
//                   <TableCell component="th" scope="row">Flood Events Observed</TableCell>
//                   <TableCell>{p.flood_event_count}</TableCell>
//                 </TableRow>
//               )}
//               {p?.max_depth_inches_of_last_flood != null && (
//                 <TableRow>
//                   <TableCell component="th" scope="row">Max Depth (Last Flood)</TableCell>
//                   <TableCell>{Number(p.max_depth_inches_of_last_flood).toFixed(1)} in</TableCell>
//                 </TableRow>
//               )}
//               {p?.max_depth_inches_of_any_flood != null && (
//                 <TableRow>
//                   <TableCell component="th" scope="row">Max Depth (Any Flood)</TableCell>
//                   <TableCell>{Number(p.max_depth_inches_of_any_flood).toFixed(1)} in</TableCell>
//                 </TableRow>
//               )}
//               {p?.last_flood_time && (
//                 <TableRow>
//                   <TableCell component="th" scope="row">Last Flood</TableCell>
//                   <TableCell>{new Date(p.last_flood_time).toLocaleString()}</TableCell>
//                 </TableRow>
//               )}
//               {p?.last_flood_end_time && (
//                 <TableRow>
//                   <TableCell component="th" scope="row">Last Flood End</TableCell>
//                   <TableCell>{new Date(p.last_flood_end_time).toLocaleString()}</TableCell>
//                 </TableRow>
//               )}
//             </TableBody>
//           </Table>  
//   );
// });